import {closeCurrentDialog, FormSubmitHandler, openDialog} from 'platform/components';

import {isNil} from 'ramda';

import {
  CustomerRequestBody,
  CustomerResponseBodyV2,
  useLazyGetCustomerV2Query,
  usePatchCustomerV2Mutation,
  useSetPreferredLocaleMutation,
} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';

import {ApiError, Nullish} from 'shared/web';

import {EditLegalPersonForm} from '../components/EditLegalPersonForm/EditLegalPersonForm';
import {EditPhysicalPersonForm} from '../components/EditPhysicalPersonForm/EditPhysicalPersonForm';
import {getCustomerRequestBody} from '../utils/getCustomerRequestBody';
import {handleApiError} from '../utils/handleApiError';
import {isCustomerNaturalPerson} from '../utils/isCustomerNaturalPerson';
import {mapCustomerValidationErrors} from '../utils/mapCustomerValidationErrors';
import {useWarnBeforeCustomerEdit} from './useWarnBeforeCustomerEdit';

export const useEditCustomer = (
  customer: CustomerResponseBodyV2 | Nullish,
  afterEdit?: (customer: CustomerResponseBodyV2) => void,
  options?: {shouldWarnBeforeActiveBusinessCaseEdit?: boolean}
) => {
  const [getCustomer, {isLoading: isCustomerLoading}] = useLazyGetCustomerV2Query();
  const [patchCustomer, {isLoading: isCustomerPatchLoading}] = usePatchCustomerV2Mutation();
  const [setPreferredLocale] = useSetPreferredLocaleMutation();
  const warnBeforeCustomerEdit = useWarnBeforeCustomerEdit();

  if (isNil(customer)) {
    return;
  }

  const onSubmit: FormSubmitHandler<CustomerRequestBody> = async (values, setErrors) => {
    const {customerRequestBody, emailFormIndexes} = getCustomerRequestBody(values);

    await patchCustomer({customerId: customer.id, customerRequestBody})
      .unwrap()
      .then(() =>
        setPreferredLocale({
          customerId: customer.id,
          setPreferredLocaleRequestBody: {
            locale: values.locale,
          },
        })
          .unwrap()
          .catch(handleApiError)
      )
      .then(() => getCustomer({customerId: customer.id}).unwrap())
      .then(afterEdit)
      .then(closeCurrentDialog)
      .catch((error: ApiError) =>
        handleApiError(error, {
          silentOnValidationError: true,
          setErrors: (errors) =>
            setErrors(
              mapCustomerValidationErrors(errors, {
                emailsPath: 'foundingPerson.emails',
                emailFormIndexes,
              })
            ),
        })
      );
  };

  const openEditCustomerDialog = () =>
    openDialog(
      isCustomerNaturalPerson(customer) ? (
        <EditPhysicalPersonForm
          isLoading={isCustomerPatchLoading || isCustomerLoading}
          customer={customer}
          onSubmit={onSubmit}
          data-testid={testIds.customer.detail('physicalPersonForm')}
        />
      ) : (
        <EditLegalPersonForm
          isLoading={isCustomerPatchLoading || isCustomerLoading}
          customer={customer}
          onSubmit={onSubmit}
          data-testid={testIds.customer.detail('legalPersonForm')}
        />
      ),
      {
        title: i18n.t('entity.customer.labels.editCustomerInformation'),
        withAdditionalFooter: true,
        scrollBehavior: isCustomerNaturalPerson(customer) ? 'inside' : 'outside',
        'data-testid': testIds.customer.detail('editCustomerInformation'),
      }
    );

  return async () => {
    if (!options?.shouldWarnBeforeActiveBusinessCaseEdit) {
      openEditCustomerDialog();
      return;
    }

    await warnBeforeCustomerEdit(customer.id, openEditCustomerDialog);
  };
};
